import { useMemo } from "react";
import { ClientByTokenQuery } from "@/graphql/queries/clientByToken.graphql.types";
import useClientFormsByToken from "@/hooks/client/useClientFormsByToken";
import { isFormArchived, shouldShowFormExpiredToClient } from "@/utils/forms";

type Visit =
  ClientByTokenQuery["clientAccessTokenByPk"]["client"]["visits"][number];

export default function useUnsignedForms({
  visits,
  forms,
}: {
  visits: Visit[];
  forms: ReturnType<typeof useClientFormsByToken>["forms"];
}) {
  const servicesInFutureVisits = useMemo(
    () =>
      new Set(
        visits.flatMap((visit) =>
          visit.serviceMenuItemLines.map((line) => line.serviceMenuItem.id)
        )
      ),
    [visits]
  );

  const offeringsInFutureVisits = useMemo(
    () =>
      new Set(
        visits.flatMap((visit) =>
          visit.serviceMenuItemLines.flatMap((line) =>
            line.serviceMenuItem.medspaServiceOfferings.map(
              ({ medspaserviceofferingId }) => medspaserviceofferingId
            )
          )
        )
      ),
    [visits]
  );

  const unsignedForms = useMemo(() => {
    if (!forms || !forms.intakeForms || !forms.consentForms) return [];

    const intakeFormsToShow = forms.intakeForms
      .filter((intakeForm) => {
        if (intakeForm.form.allServices) return true;

        return intakeForm.form.medspaServiceOfferings
          .map(({ medspaserviceofferingId }) => medspaserviceofferingId)
          .some((offeringId) => offeringsInFutureVisits.has(offeringId));
      })
      .filter(
        (intakeForm) =>
          !intakeForm.submittedAt ||
          (!isFormArchived(intakeForm) &&
            shouldShowFormExpiredToClient(intakeForm))
      )
      .map((form) => ({
        name: form.form.title,
        id: form.id,
        url:
          `intake-form/${form.id}` +
          (form.versions.length ? `?version=${form.versions.length}` : ""),
      }));

    const consentFormsToShow = forms.consentForms
      .filter((consentForm) => {
        return consentForm.consentForm.serviceMenuItems
          .map((service) => service.serviceMenuItem.id)
          .some((serviceId) => servicesInFutureVisits.has(serviceId));
      })
      .filter(({ signedAt }) => !signedAt)
      .map((form) => ({
        name: form.consentForm.name,
        id: form.id,
        url: `consent-form/${form.id}`,
      }));

    return [...intakeFormsToShow, ...consentFormsToShow];
  }, [forms, servicesInFutureVisits, offeringsInFutureVisits]);

  return { unsignedForms };
}
